import { Verdict } from '@/helpers/ratingState';

/**
 * Status sprawdzenia kraju
 */
export enum CountryCheckStatus {
  OK = 'ok',
  FOUND = 'found',
  UNKNOWN = 'unknown',
}

/**
 * Dane kraju
 */
export interface CountryData {
  /**
   * Kod kraju (ISO 3166-1 alpha-2)
   */
  code: string;
  /**
   * Nazwa kraju
   */
  name: string;
  /**
   * Rekomendacja
   */
  recommendation?: string;
  /**
   * URL do ikony flagi
   */
  flagUrl?: string;

  /**
   * Status sprawdzenia
   */
  status: CountryCheckStatus;
}

export interface CardCountryI18n {
  /**
   * Recommendation label
   * @default "Rekomendacja:"
   */
  recommendationLabel?: string;
  /**
   * Flag alt text template
   * Use {country} placeholder for country name
   * @default "Flaga {country}"
   * @example "Flaga {country}" → "Flaga Polska"
   */
  flagAltTemplate?: string;
  /**
   * Status text for OK (not recorded)
   * @default "Nie odnotowano"
   */
  statusNotRecorded?: string;
  /**
   * Status text for FOUND (recorded)
   * @default "Odnotowano"
   */
  statusRecorded?: string;
  /**
   * Status text for UNKNOWN (no data)
   * @default "Brak danych"
   */
  statusNoData?: string;
  /**
   * Comment text
   * @default "Ewidencja pojazdów skradzionych"
   */
  comment?: string;
}

/**
 * Właściwości komponentu CardCountry
 */
export interface CardCountryProps {
  /**
   * Dane kraju
   */
  country: CountryData;
  /**
   * Status sprawdzenia
   */
  status: CountryCheckStatus;
  /**
   * Rekomendacja
   */
  recommendation?: string;
  /**
   * Dodatkowe klasy CSS
   */
  className?: string;
  /**
   * Internationalization texts
   */
  i18n?: CardCountryI18n;
}

/**
 * Mapowanie statusu sprawdzenia na Verdict
 */
export const getCountryVerdict = (status: CountryCheckStatus): Verdict => {
  switch (status) {
    case CountryCheckStatus.OK:
      return Verdict.POSITIVE;
    case CountryCheckStatus.FOUND:
      return Verdict.NEGATIVE;
    case CountryCheckStatus.UNKNOWN:
      return Verdict.INFO;
    default:
      return Verdict.POSITIVE;
  }
};

/**
 * Domyślny tekst dla status sprawdzenia
 * @param status - Status sprawdzenia kraju
 * @param i18n - Opcjonalne tłumaczenia tekstów statusów
 * @returns Tekst statusu w odpowiednim języku
 */
export const getCountryStatusText = (
  status: CountryCheckStatus,
  i18n?: CardCountryI18n,
): string => {
  switch (status) {
    case CountryCheckStatus.OK:
      return i18n?.statusNotRecorded || 'Nie odnotowano';
    case CountryCheckStatus.FOUND:
      return i18n?.statusRecorded || 'Odnotowano';
    case CountryCheckStatus.UNKNOWN:
      return i18n?.statusNoData || 'Brak danych';
    default:
      return i18n?.statusNotRecorded || 'Nie odnotowano';
  }
};
